import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { first, switchMap, tap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { TemplateFacade } from "src/app/services/template.facade";
import { SubSink } from "subsink";
import {
  AbstractUserService,
  ICirculationTemplate,
  ICirculationTemplateStep,
  IPerson
} from "../../../../interfaces";
import { LogClass, Logger } from "../../../../logger";
import { NavigationService } from "../../../../services";
import { STEPTYPE_FORCONFIRMATION } from "../../constants";

@Component({
    selector: "circ-create-template",
    templateUrl: "./create-template.component.html",
    styleUrls: ["./create-template.component.scss"],
    standalone: false
})
@LogClass({ source: "stepForInformation/CreateTemplateComponent" })
export class CreateTemplateComponent implements OnInit, OnDestroy {
  public form: FormGroup;

  public saving = false;

  private template: ICirculationTemplate;
  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private templateFacade: TemplateFacade,
    private userService: AbstractUserService,
    private navigate: NavigationService,
    private translationService: I18nService
  ) {
    this.logger.debug("ctor");
    this.createForm();
  }

  public abort(): void {
    this.navigate.toTemplate(this.template.id);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.logger.info("init");

    this.templateFacade
      .getTemplateId()
      .pipe(
        tap((t) => {
          if (!t) {
            throw new Error("no Template given");
          }
        }),
        switchMap(() => this.templateFacade.getCurrentTemplate()),
        first()
      )
      .subscribe((template) => {
        this.template = template;
      });

    this.translationService
      .get("step_forConfirmation_new_name")
      .pipe(first())
      .subscribe((x) => {
        this.form.patchValue({
          title: x
        });
      });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    if (!this.template.steps) {
      this.template.steps = [];
    }

    this.saving = true;
    this.logger.info("saving");

    const logins: string[] = this.form.value.responsible.map(
      (x: IPerson) => x.login
    );

    this.userService.getUsers(logins).subscribe((users) => {
      const step: ICirculationTemplateStep = {
        stepNo: this.template.steps.length + 1,
        tasks: {
          firstResponseApplies: this.form.value.firstResponseApplies,
          assignedTo: users
        },
        duration: this.form.value.duration,
        payload: {
          stepType: STEPTYPE_FORCONFIRMATION,
          data: {
            title: this.form.value.title,
            comment: this.form.value.comment
          }
        },
        permissions: {
          canAddDocuments: this.form.value.canAddDocuments
        }
      };

      this.template.steps.push(step);

      this.subscriptions.sink = this.templateFacade
        .save(this.template)
        .subscribe((id) => {
          this.logger.info("saved:" + id);

          this.navigate.toTemplate(id);
        });
    });
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl(""),
      responsible: new FormControl([]),
      comment: new FormControl(""),
      duration: new FormControl(2, [Validators.required]),
      firstResponseApplies: new FormControl(false),
      canAddDocuments: new FormControl(false)
    });
  }
}
